/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Checks if an element contains the event's target.
 */
function elementContainsTarget(event: Event, element: HTMLElement): boolean {
    const target = event.target as HTMLElement;
    return element.contains(target);
}

/**
 * Checks if an event has not been handled yet (not defaultPrevented).
 */
function isUnhandledEvent(event: Event): boolean {
    return !event.defaultPrevented;
}

/**
 * Checks if there are no open MatDialog instances.
 */
function hasNoOpenDialogs(matDialog: MatDialog): boolean {
    return matDialog.openDialogs.length === 0;
}

/**
 * Configuration options for fromEventElementContainsTarget.
 */
export interface EventElementContainsTargetConfig extends AddEventListenerOptions {
    /**
     * Whether to check if event.defaultPrevented is true and skip those events.
     * @default true
     */
    checkDefaultPrevented?: boolean;

    /**
     * Whether to check for open MatDialog instances and filter out events when dialogs are open.
     * Only applies if matDialog parameter is provided.
     * @default true
     */
    checkMatDialog?: boolean;
}

/**
 * Determines if an event is relevant based on multiple filtering criteria.
 * Checks if the event originates within the specified element, has not been prevented,
 * and whether dialogs are open.
 *
 * @param event The event to check
 * @param element The element to scope events to
 * @param matDialog MatDialog instance to check for open dialogs
 * @param config Optional configuration for filtering
 * @returns true if event is relevant, false if it should be filtered out
 */
function isRelevantEvent(
    event: Event,
    element: HTMLElement,
    matDialog?: MatDialog,
    config?: EventElementContainsTargetConfig
): boolean {
    const { checkDefaultPrevented = true, checkMatDialog = true } = config || {};

    // Check if event originates from within scope
    if (!elementContainsTarget(event, element)) {
        return false;
    }

    // Check if event was already prevented
    if (checkDefaultPrevented && !isUnhandledEvent(event)) {
        return false;
    }

    // Check if dialogs are open
    if (checkMatDialog && matDialog && !hasNoOpenDialogs(matDialog)) {
        return false;
    }

    return true;
}

/**
 * Creates an observable that listens for events on the document while filtering them to a specific scope.
 *
 * This utility is designed to work around TalkBack accessibility issues where adding event listeners
 * directly to elements can make them focusable by screen readers when they shouldn't be. By listening
 * on the document and filtering to scope, we avoid this problem.
 *
 * The function checks if the specified element contains the event target (i.e., the event originated
 * within the element's subtree).
 *
 * This function automatically handles NgZone integration by running event listeners outside Angular's
 * zone for performance, while emitting events inside the zone to ensure proper change detection.
 *
 * @example
 * ```typescript
 * fromEventElementContainsTarget<KeyboardEvent>(
 *   this.elementRef.nativeElement,
 *   'keydown',
 *   this.ngZone,
 *   this.matDialog,
 *   { passive: true, capture: true }
 * ).pipe(
 *   filter(event => event.key === 'Escape'),
 *   takeUntil(this.destroy$)
 * ).subscribe(event => {
 *   this.onEscape(event);
 * });
 * ```
 *
 * @param scope The HTMLElement to scope events to. Events will only be emitted if the
 *              element contains the event target (i.e., the event originates from within
 *              this element's subtree)
 * @param eventType The event type to listen for (e.g., 'keydown', 'mousedown')
 * @param ngZone NgZone instance to manage Angular's change detection and optimize performance
 * @param matDialog MatDialog instance to check for open dialogs. If provided and
 *                  checkMatDialog is true, events will be filtered out when dialogs are open
 * @param config Optional configuration including event listener options (passive, capture,
 *               etc.) and filtering options
 * @returns Observable that emits events where the element contains the event target
 */
export function fromEventElementContainsTarget<T extends Event = Event>(
    scope: HTMLElement,
    eventType: string,
    ngZone: NgZone,
    matDialog?: MatDialog,
    config?: EventElementContainsTargetConfig
): Observable<T> {
    const { checkDefaultPrevented = true, checkMatDialog = true, ...eventListenerOptions } = config || {};

    return new Observable<T>(observer => {
        return ngZone.runOutsideAngular(() => {
            const subscription = fromEvent<T>(document, eventType, eventListenerOptions)
                .pipe(
                    filter(event =>
                        isRelevantEvent(event, scope, matDialog, {
                            checkDefaultPrevented,
                            checkMatDialog
                        })
                    )
                )
                .subscribe(event => {
                    ngZone.run(() => {
                        observer.next(event);
                    });
                });

            return () => subscription.unsubscribe();
        });
    });
}
